import type { WorkoutDetail } from '../services/api';

export const shareMetricIds = ['distance', 'duration', 'power', 'np', 'tss', 'ascent', 'wkg'] as const;
export type ShareMetricId = typeof shareMetricIds[number];
export type ShareMetric = { id: ShareMetricId; value: string; unit: string; label: string };
export const shareTemplates = ['midnight', 'paper', 'effort', 'ascent', 'split', 'photo', 'topo', 'ribbon', 'editorial'] as const;
export type ShareTemplate = typeof shareTemplates[number];
export const shareHeight = (template: number) => template >= 4 ? 1920 : 1350;
export function shareRouteBox(template: number) {
  return [
    [230,220,700,670], [230,220,700,670], [740,220,245,320], [160,490,750,480],
    [160,610,730,400], [100,230,880,830], [95,300,890,990], [80,270,590,620], [55,280,530,1120],
  ][template] ?? [230,220,700,670];
}
export function latinPosterDigits(value: string) {
  return value.replace(/[۰-۹٠-٩]/g, digit => String(digit.charCodeAt(0) - (digit >= '۰' ? 0x06f0 : 0x0660))).replace(/٫/g, '.').replace(/٬/g, ',');
}
export function buildShareMetrics(workout: WorkoutDetail, _locale: string, label: (id: ShareMetricId) => string): ShareMetric[] {
  const number = (v: unknown, digits = 0) => v == null || v === '' || !Number.isFinite(Number(v)) ? '—' : new Intl.NumberFormat('en-US', {maximumFractionDigits:digits}).format(Number(v));
  const sec = workout.total_time;
  const time = sec == null || !Number.isFinite(Number(sec)) ? '—' : (() => {
    const n = Math.max(0, Math.round(Number(sec)));
    const f = new Intl.NumberFormat('en-US', {minimumIntegerDigits:2, useGrouping:false});
    return `${number(Math.floor(n/3600))}:${f.format(Math.floor(n/60)%60)}:${f.format(n%60)}`;
  })();
  const values: Record<ShareMetricId, [string,string]> = {
    distance:[number(workout.distance == null ? null : workout.distance/1000),'km'],
    duration:[time,''], power:[number(workout.power_average ?? workout.details?.min_max_avg?.power?.avg),'W'],
    np:[number(workout.np),'W'], tss:[number(workout.tss_actual),''], ascent:[number(workout.total_ascent),'m'], wkg:[number(workout.wkg,2),'W/kg'],
  };
  return shareMetricIds.map(id=>({id,value:values[id][0],unit:values[id][1],label:label(id)}));
}
export function selectShareMetrics(metrics: ShareMetric[], visible: ShareMetricId[], hero: ShareMetricId | null) {
  const selected = metrics.filter(m=>visible.includes(m.id));
  return { hero:selected.find(m=>m.id===hero) ?? null, secondary:selected.filter(m=>m.id!==hero) };
}

/** A reserved brand strip in each story layout, outside route and metric columns. */
export function shareBrandBox(template: number, aspectRatio: number) {
  const width=240, height=width/aspectRatio;
  if(shareHeight(template)!==1920) return {x:64,y:70,width,height};
  const y=({4:1060,5:1110,6:1320,7:880,8:1450} as Record<number,number>)[template] ?? 1110;
  // Editorial metrics occupy the right column: align branding with the map column.
  return {x:template===8?200:420,y,width,height};
}
